import React, { useMemo, type MouseEventHandler, type Ref } from 'react';
import { styled } from '@compiled/react';
import { singleLineClamp } from '@jira/platform__single-line-clamp-mixin';
import { Anchor, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import {
	asColor,
	getLozengeThemeByColor,
} from '@atlassian/jira-issue-epic-color/src/common/utils.tsx';
import type { HoverPopoverContentRef } from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import messages from './messages.tsx';

type Props = {
	issueKey: string;
	label: string;
	colorKey?: string | null;
	disableClickAction?: boolean;
	onClick?: MouseEventHandler;
	/**
	 * When `true` the link text will cut off and show ellipsis if it exceeds the available space.
	 * When `false` the link text will break onto the next line.
	 */
	isTruncated: boolean;
	contentRef?: Ref<HTMLAnchorElement> | HoverPopoverContentRef | null;
};

export const Summary = ({
	disableClickAction,
	onClick,
	label,
	colorKey,
	issueKey,
	isTruncated,
	contentRef,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const lozengeTheme = useMemo(
		() => (colorKey ? getLozengeThemeByColor(asColor(colorKey)) : undefined),
		[colorKey],
	);

	if (disableClickAction) {
		return (
			<WrapperNoClick
				// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={lozengeTheme}
			>
				{label}
			</WrapperNoClick>
		);
	}

	if (onClick != null) {
		return (
			<ButtonText
				onClick={onClick}
				// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={lozengeTheme}
			>
				{label}
			</ButtonText>
		);
	}

	return (
		<Anchor
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			testId="issue-field-parent.ui.view-link"
			href={`/browse/${issueKey}`}
			aria-label={formatMessage(messages.parentTitle, { label })}
			// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop
			style={lozengeTheme}
			xcss={[
				linkTextStylesBase,
				isTruncated ? linkTextStylesTruncated : linkTextStylesNonTruncated,
			]}
			ref={contentRef}
		>
			{label}
		</Anchor>
	);
};

const linkTextStylesBase = xcss({
	borderRadius: token('radius.small'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '16px',
	paddingInline: 'space.050',
	paddingBlock: 'space.025',
	textDecoration: 'none',
	':hover': {
		textDecoration: 'underline',
	},
});

const linkTextStylesNonTruncated = xcss({
	overflowWrap: 'anywhere',
});

const linkTextStylesTruncated = xcss({
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const ButtonText = styled.button(singleLineClamp, {
	// Disable buttons styles
	border: 'none',
	background: 'none',
	color: 'inherit',
	font: token('font.body'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '16px',
	outline: 'inherit',
	// Add custom styles
	cursor: 'pointer',
	height: '20px',
	maxWidth: '100%',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	paddingTop: token('space.025'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.050'),
	'&:hover': {
		textDecoration: 'underline',
	},
	'&:focus': {
		outline: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
const WrapperNoClick = styled.span(singleLineClamp, {
	display: 'block',
	font: token('font.body'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	lineHeight: '16px',
	height: '16px',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	paddingTop: token('space.025'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.050'),
	maxWidth: '100%',
});
